import {
  Building,
  PvPanel,
} from "@inrange/building-manager-api-client/models-site";
import axios from "axios";
import { Feature, LineString, Polygon } from "geojson";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { queryOptions } from "./utils";

const pvaHostnameMap: Record<string, string> = {
  prod: "https://7rbvbbr755.execute-api.us-east-1.amazonaws.com",
  staging: "https://nh41zqd7q4.execute-api.us-east-1.amazonaws.com",
  "inrt-1466": "https://32buq9xfy3.execute-api.eu-west-1.amazonaws.com", // dev-martin
  "inrt-1468": "https://32buq9xfy3.execute-api.eu-west-1.amazonaws.com", // dev-martin
  "inrt-2024": "https://32buq9xfy3.execute-api.eu-west-1.amazonaws.com", // dev-martin
  // Rooftop engine V2. The QA env for this branch must talk to the pv-api that HAS the V2
  // detector — dev-martin — or it silently tests the old one against staging and proves nothing.
  // The key is what getPVAUrl() derives from VITE_BRANCH_NAME: the first two dash-segments of the
  // slugified branch (claude/rooftop-engine-v2-admin-controls -> "claude-rooftop").
  // Inert after merge: on main, VITE_BRANCH_NAME is "main", which is not in this map, so it falls
  // through to staging exactly as before. Safe to leave; safe to delete.
  "claude-rooftop": "https://32buq9xfy3.execute-api.eu-west-1.amazonaws.com", // dev-martin
};

export const getPVAUrl = (): string => {
  const isProduction = import.meta.env.VITE_KINDE_AUTH_ENV === "PROD";
  if (isProduction) {
    return pvaHostnameMap.prod;
  }

  const branchName = import.meta.env?.VITE_BRANCH_NAME;
  const ticketNo = branchName?.split("-").slice(0, 2).join("-");
  return (!!ticketNo && pvaHostnameMap[ticketNo]) || pvaHostnameMap.staging;
};

async function getPvGeoJson(
  siteId: string,
  panel: { width: number; height: number },
  buildingIds: string
): Promise<{
  panelsData: any;
  isAlignedPanelData: boolean;
  site_image?: SiteImage;
  building_shifts?: Record<string, [number, number]>;
}> {
  const pvaUrl = getPVAUrl();
  const url = `${pvaUrl}/pv-geojson?siteid=${siteId}&panel=${JSON.stringify(panel)}&building_ids=${buildingIds}`;

  const panelsPointerResponse = await axios.get(url, { timeout: 30_000 });

  const panelsUrl = panelsPointerResponse.data?.panels;
  const panelsAlignedUrl = panelsPointerResponse.data?.panels_aligned;
  const siteImage = panelsPointerResponse.data?.site_image;
  const buildingShifts = panelsPointerResponse.data?.building_shifts;

  if (!panelsUrl) {
    return { panelsData: [], isAlignedPanelData: false, site_image: siteImage };
  }

  const isAlignedPanelData = !!(siteImage && panelsAlignedUrl);
  const panelsResponse = await axios.get(
    isAlignedPanelData ? panelsAlignedUrl : panelsUrl,
    {
      timeout: 30_000,
    }
  );

  return {
    panelsData: panelsResponse.data,
    isAlignedPanelData,
    site_image: siteImage,
    building_shifts: buildingShifts,
  };
}

export const usePvGeoJson = (
  siteId: string | undefined,
  panel: PvPanel | undefined,
  buildingIds: string | undefined
): UseQueryResult<{
  panelsData: any;
  isAlignedPanelData: boolean;
  site_image?: SiteImage;
  building_shifts?: Record<string, [number, number]>;
}> => {
  return useQuery(
    ["pv_geo_json", siteId, panel, buildingIds],
    async () => {
      return await getPvGeoJson(siteId!, panel!, buildingIds!);
    },
    queryOptions({
      // Allow query to be mounted multiple times on the same page without re-fetching
      staleTime: 5 * 60 * 1000,
      enabled: !!siteId && !!panel && !!buildingIds,
    })
  );
};

export interface SiteImage {
  image_png: string;
  image_jpg: string;
  cached_at: string;
  bounds: [number, number, number, number];
}

export interface SiteData {
  id: string;
  name: string;
  user: string;
}
export type RooftopEngineVersion = "auto-V1" | "auto-V2" | "manual";

// Roof-object classification. This union is the UNION of two vocabularies, which is why it is
// bigger than either.
//
// WHAT THE BACKEND ACTUALLY PUTS ON THE WIRE -- exactly two values, for BOTH engines:
//
//   "skylight"  the object joined a confirmed skylight row. Engine V2 only; that grid membership
//               is the evidence, and it is the only type the detector ever earns.
//   "other"     everything else auto-detected. Engine V2 flattens its internal UNKNOWN to this at
//               the storage seam (object_detection_v2/__init__.py), and engine V1 (Canny) cannot
//               classify at all, so every V1 object is "other" too.
//
// So "other" means BOTH "the detector found a thing and cannot say what" AND "a human said Other".
// Do not try to tell them apart by type. `properties.source` is the ONLY field that carries
// provenance -- see PVSystem.tsx, and the counters in PVCustomObjectsModal/PvDesignControls.
//
// WHAT A HUMAN CAN ASSIGN, via the type dropdown: hvac / roofedge / solarpanel / other. The
// detector never produces the first three.
//
// "autodetected" is the legacy value: older backends sent untyped objects and PVSystem.tsx still
// stamps that on anything arriving without a type.
//
// "unknown" is here defensively and is NOT currently emitted: it is the backend's internal
// ObjectType.UNKNOWN, which the storage seam flattens to "other". If that flattening is ever
// removed, "unknown" arrives and everything here already handles it (label, counters, dropdown
// guard, toCustomObjectType). Do not write code that ASSUMES it arrives -- it does not, today.
export type RoofObjectType =
  | "skylight"
  | "unknown"
  | "hvac"
  | "roofedge"
  | "solarpanel"
  | "other"
  | "autodetected";

// Auto-detected roof objects are delivered (via a presigned URL) as GeoJSON
// Features carrying the detected type in properties.type, nested one array per
// building. Legacy (rooftop engine V1 / older backends) delivered bare geometry
// strings instead — PVSystem.tsx tolerates both.
export type RoofObjectFeature = Feature<Polygon | LineString> & {
  properties: { type?: RoofObjectType };
};

export interface PvaData {
  status?: {
    num_buildings?: number;
    num_buildings_triggered?: number;
    num_buildings_packed?: number;
  };
  // The latest available *auto* rooftop engine (a server constant, e.g.
  // "auto-V2"). Absent on older API responses.
  latest_rooftop_engine_version?: string;
  // How the current design was built: "auto-V1" (Canny), "auto-V2" (SAM) or
  // "manual" (user-supplied roof objects). Absent on older API responses.
  built_with_rooftop_engine_version?: RooftopEngineVersion;
  site_metrics?: {
    total_mass: number;
    net_force: number;
    dead_load: number;
    roof_allocation: number;
    num_panels: number;
    total_pnom: number;
  };
  task?: {
    status: string;
    started_at: string;
  };
  task_metrics?: {
    task_run_success: boolean;
    pv_design_available: boolean;
    // Failure reason from the last run (null on success). Used to report
    // *why* a PVA task failed without re-triggering it.
    error?: string | null;
    start_time: string;
    run_time: number;
    inputs: {
      options: { [key: string]: string };
    };
  };
  debug?: {
    [key: string]: {
      num_panels_before_object_intersection: number;
      num_panels_after_object_intersection: number;
      building_with_objects_image: string;
      building_with_panels_with_objects_image: string;
    };
  };
  geojson?: {
    panels_aligned: string;
    roof_objects_autodetected: string;
  };
  custom_objects?: (Feature<Polygon> | Feature<LineString>)[];
  site_image?: SiteImage;
}

export async function requestPvSystemDesign(
  siteData: SiteData,
  panel: PvPanel,
  buildings: Building[],
  options: Record<string, unknown>,
  customObjects:
    | (Feature<Polygon> | Feature<LineString>)[]
    | undefined = undefined,
  enableTestModePVARequests: boolean
): Promise<{ data: PvaData }> {
  const data = {
    site: siteData,
    panel,
    buildings,
    custom_objects: customObjects,
    options,
  };

  const pvaUrl = enableTestModePVARequests
    ? "https://pva-e2e.com/"
    : getPVAUrl();
  const url = `${pvaUrl}/pv-design?siteid=${siteData.id}`;
  return await axios.post(url, data, { timeout: 30_000 });
}

export const usePvSystemDesign = (
  siteData: SiteData,
  panel: PvPanel | undefined,
  buildings: Building[],
  autoRefresh: boolean,
  enableTestModePVARequests: boolean
): {
  query: UseQueryResult<{ data: PvaData }>;
  requestPvSystemDesign: typeof requestPvSystemDesign;
} => {
  return {
    query: useQuery(
      ["pv_system_design", siteData?.id, panel, autoRefresh, buildings],
      async () => {
        return await requestPvSystemDesign(
          siteData!,
          panel!,
          buildings,
          {},
          undefined,
          enableTestModePVARequests
        );
      },
      {
        enabled: !!siteData && !!panel && !!buildings && buildings?.length > 0,
        refetchInterval: autoRefresh ? 30_000 : false,
        refetchOnWindowFocus: autoRefresh,
      }
    ),
    requestPvSystemDesign,
  };
};

export const usePresignedUrlData = <T>(
  siteId: string,
  urlType: string,
  url: string | undefined
): UseQueryResult<T> => {
  return useQuery(
    [siteId, urlType],
    async (): Promise<T> => {
      const response = await axios.get(url!, { timeout: 30_000 });
      return await response.data;
    },
    {
      enabled: !!url,
    }
  );
};
